import { z } from 'zod';

/**
 * site.json スキーマ v1 (計画書§5)。
 * スキーマを変更するときは必ず schemaVersion を上げること(CLAUDE.md参照)。
 */

const EnuPositionSchema = z.object({
  east: z.number(),
  north: z.number(),
  up: z.number(),
});

const EntitySchema = z.object({
  model: z.string(),
  position: EnuPositionSchema,
  rotationYDeg: z.number().default(0),
  scale: z.number().positive().default(1),
  /** 再生するAnimationClip名。"*" = 全クリップ */
  animation: z.string().default('*'),
  /**
   * 指定すると、このエンティティが視野外のとき画面端に方向矢印+このラベルを
   * 表示する(「どこにいるか分からない=消えた」と誤認されるのを防ぐ)。
   * (省略可能フィールドの追加はv1互換)
   */
  indicator: z.string().optional(),
  /**
   * trueにすると実行時の見かけスケール調整(視角補正+ユーザースライダー)の
   * 対象になる。スケールはジオメトリにのみ適用され、軌道は変わらない。
   * モデルは実寸で制作しておくこと。(省略可能フィールドの追加はv1互換)
   */
  visualScale: z.boolean().default(false),
});

const SceneSchema = z.object({
  id: z.string(),
  /** ループ1周期[s]。アニメと音声はこの長さに揃えて制作する */
  loopSeconds: z.number().positive(),
  /** 音声候補。先頭から順にデコードを試み、最初に成功したものを使う */
  audio: z.array(z.string()).default([]),
  entities: z.array(EntitySchema).min(1),
  occluders: z.array(z.string()).default([]),
});

export const SiteManifestSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  title: z.string(),
  description: z.string().default(''),
  origin: z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    alt: z.number().default(0),
    altMode: z.enum(['absolute', 'relativeToGround']).default('relativeToGround'),
    /**
     * 'viewer': 起動時の現在地を原点にする(どこでも体験できる)。
     * 'fixed'(デフォルト): lat/lonの実座標にアンカーする。
     * クエリ ?origin=here / ?origin=fixed で上書き可。
     * (省略可能なdefault付きフィールドの追加はv1互換)
     */
    anchor: z.enum(['fixed', 'viewer']).default('fixed'),
  }),
  geofence: z.object({ radiusMeters: z.number().positive() }).default({ radiusMeters: 300 }),
  /** 磁気偏角[deg]。東偏が正(日本は約-7〜-8) */
  magneticDeclinationDeg: z.number().default(0),
  scenes: z.array(SceneSchema).min(1),
});

export type SiteManifest = z.infer<typeof SiteManifestSchema>;
export type SiteScene = SiteManifest['scenes'][number];
export type SiteEntity = SiteScene['entities'][number];

/** /sites/<id>/site.json をロードしてバリデーションする */
export async function loadSiteManifest(siteId: string): Promise<{ manifest: SiteManifest; baseUrl: string }> {
  const baseUrl = `/sites/${encodeURIComponent(siteId)}/`;
  const res = await fetch(`${baseUrl}site.json`);
  if (!res.ok) {
    throw new Error(`site.json の取得に失敗しました (${res.status}): ${baseUrl}site.json`);
  }
  const json: unknown = await res.json();
  const parsed = SiteManifestSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`site.json が不正です: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`);
  }
  return { manifest: parsed.data, baseUrl };
}
